import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { draftStore, myStoresQuery, sellEligibilityQuery, useAcceptSellerTerms } from "@/api/queries/selling";
import type { SellEligibility, SellerStore } from "@/api/types";
import { AppHeader } from "@/components/layout";
import { Badge, Button, buttonClassName, Skeleton, Stepper } from "@/components/ui";
import {
  SELLER_TERMS_POINTS,
  SELLER_TERMS_VERSION,
  type TraderDeclaration,
} from "@/domain/selling";
import { cn } from "@/lib/cn";
import { marketplaceSearch } from "@/pages/marketplace/search";
import { useDashboardStoreSelection } from "@/stores/dashboard-store";
import { sellErrorMessage } from "./sell-errors";
import { SELL_STEPS } from "./store-values";

/** /sell — plan §4 journeys 2 and 3: eligibility, seller terms, then setup or the stores you already run. */
type AcceptMutation = ReturnType<typeof useAcceptSellerTerms>;

export function SellPage() {
  const eligibility = useQuery(sellEligibilityQuery());
  const stores = useQuery(myStoresQuery());
  // Owned by the page: accepting refreshes eligibility, and the terms step must stay on screen (rather than
  // flash the next state) until the page has moved on to /sell/new.
  const accept = useAcceptSellerTerms();

  if (!eligibility.data || !stores.data) return <SellPageSkeleton />;
  return (
    <>
      <AppHeader />
      <main className="mx-auto w-full max-w-2xl px-6 py-10">
        <SellPageContent
          eligibility={eligibility.data}
          stores={stores.data}
          accept={accept}
        />
      </main>
    </>
  );
}

function SellPageContent({
  eligibility,
  stores,
  accept,
}: {
  eligibility: SellEligibility;
  stores: SellerStore[];
  accept: AcceptMutation;
}) {
  const { missing } = eligibility;
  const draft = draftStore(stores);

  if (missing.includes("age_18_plus")) return <UnderAgeNotice />;
  if (missing.includes("verified_email")) return <VerifyEmailNotice />;
  const termsMissing = missing.includes("seller_terms") || missing.includes("trader_declaration");
  if (termsMissing || accept.isPending || accept.isSuccess) {
    return (
      <SellerTermsStep
        eligibility={eligibility}
        hasDraft={Boolean(draft)}
        accept={accept}
      />
    );
  }
  if (draft) return <ResumeDraft draft={draft} />;
  if (stores.length > 0) {
    return (
      <YourStores
        stores={stores}
        atDailyLimit={eligibility.storesCreatedToday >= eligibility.storesPerDayLimit}
      />
    );
  }
  return (
    <section>
      <h1 className="text-h3">Open your store</h1>
      <p className="mt-2 text-body text-text-secondary">
        Pick a name and a URL, tell buyers what you make, and publish when you're ready.
      </p>
      <Link
        to="/sell/new"
        className={buttonClassName({ size: "lg", className: "mt-8" })}
      >
        Set up your store
      </Link>
    </section>
  );
}

function UnderAgeNotice() {
  return (
    <section aria-labelledby="sell-under-age-title">
      <h1
        id="sell-under-age-title"
        className="text-h3"
      >
        You can open a store when you're 18
      </h1>
      <p className="mt-3 text-body text-text-secondary">
        Stores on Studside sell to the public, so every store owner needs to be an adult. We go by the date of birth
        you gave when you joined, so there's nothing to fill in here for now.
      </p>
      <p className="mt-3 text-body text-text-secondary">
        Until then you can keep sharing your work, join communities and team up with other creators.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/communities"
          className={buttonClassName({ size: "lg" })}
        >
          Explore communities
        </Link>
        <Link
          to="/marketplace"
          search={marketplaceSearch()}
          className={buttonClassName({ variant: "secondary", size: "lg" })}
        >
          Back to the marketplace
        </Link>
      </div>
    </section>
  );
}

function VerifyEmailNotice() {
  return (
    <section>
      <h1 className="text-h3">Verify your email to start selling</h1>
      <p className="mt-3 text-body text-text-secondary">
        Buyers and Studside need a way to reach every store owner. Add and verify an email address, then come back here.
      </p>
      <Link
        to="/settings/account"
        className={buttonClassName({ size: "lg", className: "mt-8" })}
      >
        Account settings
      </Link>
    </section>
  );
}

const DECLARATIONS: { value: TraderDeclaration; title: string; description: string }[] = [
  {
    value: "non_trader",
    title: "As a private person",
    description: "You make and share things outside of any business.",
  },
  {
    value: "trader",
    title: "As a business",
    description: "Selling is part of your trade, business or profession.",
  },
];

function SellerTermsStep({
  eligibility,
  hasDraft,
  accept,
}: {
  eligibility: SellEligibility;
  hasDraft: boolean;
  accept: AcceptMutation;
}) {
  const navigate = useNavigate();
  const [declaration, setDeclaration] = useState<TraderDeclaration | null>(
    eligibility.acceptance?.traderDeclaration ?? null,
  );
  const [agreed, setAgreed] = useState(false);
  const outdated = eligibility.acceptance !== null && eligibility.acceptance.termsVersion !== SELLER_TERMS_VERSION;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!declaration || !agreed || accept.isPending || accept.isSuccess) return;
    try {
      await accept.mutateAsync({ termsVersion: SELLER_TERMS_VERSION, traderDeclaration: declaration });
    } catch {
      return; // Shown below from the mutation's error.
    }
    void navigate({ to: "/sell/new" });
  };

  return (
    <section>
      <Stepper
        steps={SELL_STEPS}
        current={0}
      />
      <h1 className="mt-8 text-h3">{outdated ? "Our seller terms have changed" : "Start selling on Studside"}</h1>
      <p className="mt-2 text-body text-text-secondary">
        {hasDraft
          ? "Accept the current terms to keep setting up your store."
          : "Before you set up your store, here's how selling works."}
      </p>

      <ul className="mt-6 list-disc space-y-2 pl-5 text-body text-text-secondary marker:text-text-muted">
        {SELLER_TERMS_POINTS.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>

      <form
        className="mt-8 space-y-6"
        onSubmit={(event) => void submit(event)}
        noValidate
      >
        <fieldset>
          <legend className="mb-3 text-small font-medium text-text-primary">How do you sell?</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {DECLARATIONS.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-md border bg-surface-2 p-4 transition-colors",
                  "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary",
                  declaration === option.value ? "border-primary bg-primary-soft" : "border-border hover:border-border-strong",
                )}
              >
                <input
                  type="radio"
                  name="trader-declaration"
                  value={option.value}
                  checked={declaration === option.value}
                  onChange={() => setDeclaration(option.value)}
                  className="mt-1 accent-primary"
                />
                <span>
                  <span className="block text-body font-medium text-text-primary">{option.title}</span>
                  <span className="mt-0.5 block text-small text-text-secondary">{option.description}</span>
                </span>
              </label>
            ))}
          </div>
          <small className="mt-2 block text-caption text-text-muted">Buyers see this on your listings.</small>
        </fieldset>

        <label className="flex items-start gap-3 text-body text-text-primary">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
            className="mt-1 size-4 accent-primary"
          />
          <span>
            I accept the Studside seller terms
            <span className="block text-caption text-text-muted">Version {SELLER_TERMS_VERSION}</span>
          </span>
        </label>

        {accept.isError && (
          <p
            role="alert"
            className="text-small text-danger"
          >
            {sellErrorMessage(accept.error)}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          loading={accept.isPending || accept.isSuccess}
          disabled={!declaration || !agreed}
        >
          Accept and continue
        </Button>
      </form>
    </section>
  );
}

function ResumeDraft({ draft }: { draft: SellerStore }) {
  return (
    <section>
      <h1 className="text-h3">Pick up where you left off</h1>
      <p className="mt-2 text-body text-text-secondary">
        <span className="font-medium text-text-primary">{draft.name}</span> is saved as a draft at store/{draft.slug}.
        Nobody else can see it until you publish.
      </p>
      <Link
        to="/sell/new"
        className={buttonClassName({ size: "lg", className: "mt-8" })}
      >
        Continue setting up
      </Link>
    </section>
  );
}

function YourStores({ stores, atDailyLimit }: { stores: SellerStore[]; atDailyLimit: boolean }) {
  const select = useDashboardStoreSelection((state) => state.select);
  return (
    <section>
      <h1 className="text-h3">Your stores</h1>
      <ul className="mt-6 divide-y divide-border-subtle border-y border-border-subtle">
        {stores.map((store) => (
          <li
            key={store.id}
            className="flex flex-wrap items-center gap-3 py-4"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-body font-medium text-text-primary">{store.name}</p>
              <p className="truncate text-small text-text-secondary">store/{store.slug}</p>
            </div>
            <StoreStatusBadge status={store.status} />
            <Link
              to="/dashboard"
              onClick={() => select(store.slug)}
              className={buttonClassName({ variant: "secondary", size: "sm" })}
              aria-label={`Manage ${store.name}`}
            >
              Manage
            </Link>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        {atDailyLimit ? (
          <p className="text-small text-text-secondary">You've opened as many stores as you can today. Try again tomorrow.</p>
        ) : (
          <Link
            to="/sell/new"
            className={buttonClassName({ size: "lg" })}
          >
            Open another store
          </Link>
        )}
      </div>
    </section>
  );
}

export function StoreStatusBadge({ status }: { status: SellerStore["status"] }) {
  switch (status) {
    case "active":
      return <Badge variant="success">Live</Badge>;
    case "draft":
      return <Badge variant="neutral">Draft</Badge>;
    case "suspended":
      return <Badge variant="danger">Suspended</Badge>;
    case "archived":
      return <Badge variant="neutral">Archived</Badge>;
  }
}

export function SellPageSkeleton() {
  return (
    <>
      <AppHeader />
      <main
        className="mx-auto w-full max-w-2xl px-6 py-10"
        aria-busy="true"
      >
        <Skeleton className="h-3 w-40" />
        <Skeleton className="mt-8 h-8 w-72" />
        <Skeleton className="mt-3 h-5 w-full" />
        <Skeleton className="mt-8 h-48 w-full" />
      </main>
    </>
  );
}
